import React, { useEffect, useState, useMemo } from "react";
import { Form, Col, Row, OverlayTrigger, Tooltip } from "react-bootstrap";
import { mapUserRoles } from "../../../functions/useRoleMapping";
import NoRecordFoundAlert from "../../common/NoRecordFoundAlert";
import { useSelector } from "react-redux";
import useHandleBlur from "../../../functions/useHandleBlur";
import useTbdCheck from "../../../functions/useTbdCheck";
import useFieldClass from "../../../functions/useFieldClass";
// import { useFetchJobDetails } from "../../../functions/useFetchJobDetails"; // Reusable API hook
import { useFormState } from "../../../functions/useFormState"; // Reusable form hook
import {
  selectUserDetails,
  selectUserRoles,
} from "../../../redux/slices/userSlice";
import { selectApiUrls } from "../../../redux/slices/apiUrlSlice";
//import { setMultipleJobDetails } from "../../../redux/slices/jobDetailsSlice";
import { selectJobDetailsSection, selectFieldDescriptions } from "../../../redux/selectors";
import { selectApiStatus } from "../../../redux/slices/apiStatusSlice"; // Import the selector

function JobGeneralSpecsRF({ zone }) {
  // const dispatch = useDispatch();
  const userDetails = useSelector(selectUserDetails); // User details
  const userRoles = useSelector(selectUserRoles); // User details
  const currentUser = userDetails?.name;

  const apiUrls = useSelector(selectApiUrls); // Fetch API URLs from Redux
  const sectionName = "generalStation";
  const apiUrl = apiUrls[sectionName];
  const tableKey = sectionName;

  const { options } = useSelector((state) => state.dropdown); // Fetch dropdown values from redux

  const error = null;

  const storedJobDetails = useSelector((state) =>
    selectJobDetailsSection(state, sectionName)
  );

  const activetab = useSelector(
    (state) => state.activeMechanicalTab.selectedZone
  );
  const jobDetails = useMemo(
    () => storedJobDetails.find((detail) => detail.Zone === activetab) || {},
    [storedJobDetails, activetab]
  );

  const apiStatus = useSelector(selectApiStatus); // Fetch API status from Redux
  const loading = apiStatus[sectionName] !== "success"; // Determine loading state based on API status

  const readOnly = useSelector((state) => state.readOnly.isReadOnly); // Define readOnly

  const { formData, handleChange, setFormData } = useFormState({}); // Form state management

  const normalizeWetWellDepthToInches = (value) => {
    if (value === null || value === undefined) return "";

    const raw = String(value).trim();
    if (!raw) return "";

    const alphaOnly = raw.toLowerCase().replace(/[^a-z]/g, "");
    if (alphaOnly === "tbd") {
      return "TBD";
    }

    if (alphaOnly === "na") {
      return "N/A";
    }

    const feetMatch = raw.match(/(\d+(?:\.\d+)?)\s*(?:ft|feet|')/i);
    const inchMatch = raw.match(/(\d+(?:\.\d+)?)\s*(?:in|inch|inches|")/i);

    if (feetMatch || inchMatch) {
      const feet = feetMatch ? Number(feetMatch[1]) : 0;
      const inches = inchMatch ? Number(inchMatch[1]) : 0;
      if (Number.isFinite(feet) && Number.isFinite(inches)) {
        return String(Math.round(feet * 12 + inches));
      }
    }

    if (/^\d+$/.test(raw)) {
      return raw;
    }

    return "";
  };

  const handleWetWellDepthBlur = (e) => {
    const normalizedInches = normalizeWetWellDepthToInches(e.target.value);

    setFormData((prev) => ({
      ...prev,
      wetWellDepth: normalizedInches,
    }));

    e.target.value = normalizedInches;

    handleBlur(e, "Wet Well Depth (in)", [], {
      ID: formData.ID,
      url: apiUrl,
      field: "WetWellDepth",
      tableKey,
    });
  };

  const wetWellDepthDisplay = useMemo(() => {
    const normalized = normalizeWetWellDepthToInches(formData.wetWellDepth);
    if (!normalized) return "";

    const totalInches = Number.parseInt(normalized, 10);
    if (!Number.isFinite(totalInches)) return "";

    const feet = Math.floor(totalInches / 12);
    const inches = totalInches % 12;

    return `${feet}ft ${inches}in`;
  }, [formData.wetWellDepth]);

  // Track jobDetails changes deeply by stringifying to detect any field updates
  const jobDetailsKey = useMemo(() => JSON.stringify(jobDetails), [jobDetails]);

  useEffect(() => {
    if (jobDetails && jobDetails.ID) {
      setFormData({
        ID: jobDetails.ID || "",
        stationType: jobDetails.StationType || "",
        siteVoltage: jobDetails.SiteVoltage || "",
        stationConfiguration: jobDetails.StationConfiguration || "",
        powerConfiguration: jobDetails.PowerConfiguration || "",
        primeSystem: jobDetails.PrimeSystem || "",
        sccrRating: jobDetails.SCCRRating || "",
        dynamicInletPSI: jobDetails.DynamicInletPSI || "",
        fccType: jobDetails.FCCType || "",
        regulatoryCompliance: jobDetails.RegulatoryCompliance || "",
        feetOfLift: jobDetails.FeetOfLift || "",
        stationLocation: jobDetails.StationLocation || "",
        siteElevation: jobDetails.SiteElevation || "",
        color: jobDetails.Color || "",
        flowMeterLocation: jobDetails.FlowMeterLocation || "",
        trackerType: jobDetails.TrackerType || "",
        dischargePSI: jobDetails.DischargePSI || "",
        totalGPM: jobDetails.TotalGPM || "",
        wetWellDepth: normalizeWetWellDepthToInches(jobDetails.WetWellDepth),
        electricalEnclosureLocation:
          jobDetails.ElectricalEnclosureLocation || "",
        additionalJBox: jobDetails.AdditionalJBox || "",
        comments: jobDetails.Comments || "",
        retrofitKitType: jobDetails.RetrofitKitType || "",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobDetailsKey, setFormData]);

  const jobCheckoutStatus = useSelector((state) => state.checkout.job);
  const isJobCheckedOut = jobCheckoutStatus?.IsCheckedOut === true;
  const isCurrentUserCheckedOut =
    jobCheckoutStatus?.CheckedOutBy === currentUser;
  const isDisabled = (isJobCheckedOut && !isCurrentUserCheckedOut) || readOnly; // Update isDisabled logic

  // State to store role variables
  const [roles, setRoles] = useState({
    Admin: false,
    Apps: false,
    Engineer: false,
    TestPit: false,
    PSN: false,
    Sales: false,
    ReadOnly: false,
  });

  const { handleFocus, handleBlur, showNotification, saveStatus } =
    useHandleBlur(formData);

  const isValueInDropdown = (value, dropdownOptions) => {
    const exists = dropdownOptions.some(
      (option) => option.DisplayText === value || option.displayName === value
    );
    return exists;
  };

  const stationTypeOptions =
    options
      ?.filter((option) => option.DropdownType === "Station Type")
      .sort((a, b) => a.SortOrder - b.SortOrder) || []; // Default to an empty array if undefined
  const siteVoltageOptions =
    options
      ?.filter((option) => option.DropdownType === "Site Voltage")
      .sort((a, b) => a.SortOrder - b.SortOrder) || [];
  const stationConfigurationOptions =
    options
      ?.filter((option) => option.DropdownType === "Station Configuration")
      .sort((a, b) => a.SortOrder - b.SortOrder) || [];
  const powerConfigurationOptions =
    options
      ?.filter((option) => option.DropdownType === "Power Configuration")
      .sort((a, b) => a.SortOrder - b.SortOrder) || [];
  const primeSystemOptions =
    options
      ?.filter((option) => option.DropdownType === "Prime System")
      .sort((a, b) => a.SortOrder - b.SortOrder) || [];
  const fccTypeOptions =
    options
      ?.filter((option) => option.DropdownType === "FCC Type")
      .sort((a, b) => a.SortOrder - b.SortOrder) || [];
  const regulatoryComplianceOptions =
    options
      ?.filter((option) => option.DropdownType === "Regulatory Compliance")
      .sort((a, b) => a.SortOrder - b.SortOrder) || [];
  const stationLocationOptions =
    options
      ?.filter((option) => option.DropdownType === "Station Location")
      .sort((a, b) => a.SortOrder - b.SortOrder) || [];
  const colorOptions =
    options
      ?.filter((option) => option.DropdownType === "Color" && option.DisplayText !== "Same as Station")
      .sort((a, b) => a.SortOrder - b.SortOrder) || [];
  const flowMeterLocationOptions =
    options
      ?.filter((option) => option.DropdownType === "Flow Meter Location")
      .sort((a, b) => a.SortOrder - b.SortOrder) || [];
  const trackerTypeOptions =
    options
      ?.filter((option) => option.DropdownType === "Tracker Type")
      .sort((a, b) => a.SortOrder - b.SortOrder) || [];
  const electricalEnclosureLocationOptions =
    options
      ?.filter(
        (option) => option.DropdownType === "Electrical Enclosure Location"
      )
      .sort((a, b) => a.SortOrder - b.SortOrder) || [];
  const retrofitKitTypeOptions =
    options
      ?.filter((option) => option.DropdownType === "Retrofit Kit Type")
      .sort((a, b) => a.SortOrder - b.SortOrder) || [];
  const sccrRatingOptions =
    options
      ?.filter((option) => option.DropdownType === "SCCR Rating")
      .sort((a, b) => a.SortOrder - b.SortOrder) || [];

  useEffect(() => {
    setRoles(mapUserRoles(userRoles));
  }, [userRoles]);

  useTbdCheck(formData, zone); // Pass zone to track TBD status for specific zone

  const fieldClasses = useFieldClass(formData, zone); // Use the new function

  const handleSubmit = (e) => {
    e.preventDefault();
  };

  const fieldDescriptions = useSelector(selectFieldDescriptions);
  

  const getFieldTitle = (fieldName) => {
    return (
      fieldDescriptions.find((desc) => desc.ColumnName === fieldName)
        ?.Description || ""
    );
  };

  const renderTooltip = (fieldName) => (
    <Tooltip id={`tooltip-${fieldName}`}>
      {getFieldTitle(fieldName)
        .split("\n")
        .map((line, index) => (
          <span key={index}>
            {line}
            <br />
          </span>
        ))}
    </Tooltip>
  );

  // Determine if we should show Station Configuration based on Station Type or Tracker Type
  const shouldShowStationConfiguration = useMemo(() => {
    const stationType = formData.stationType;
    const trackerType = formData.trackerType;
    
    return stationType === "Horizontal Centrifugal" || 
           stationType === "Vertical Multistage" || 
           stationType === "WaterMax 9000" || 
           stationType === "WaterMax 7000" || 
           stationType === "WaterMax 5000" || 
           trackerType === "HC";
  }, [formData.stationType, formData.trackerType]);

  // Check if station type is Vertical Turbine (these types should hide Prime System and Dynamic Inlet PSI)
  const isVerticalTurbineType = formData.stationType === "Vertical Turbine Custom" || 
                                 formData.stationType === "Vertical Turbine Standard";
  
  // Determine what inlet/lift fields to show based on Station Configuration
  const showDynamicInletPSI = (formData.stationConfiguration === "Booster" || 
                              formData.stationConfiguration === "Flooded Suction") &&
                              !isVerticalTurbineType;
  
  const showFeetOfLift = formData.stationConfiguration === "Suction Lift";
  
  // Determine if we should show Wet Well Depth - hide if Station Configuration is shown
  const shouldShowWetWellDepth = !shouldShowStationConfiguration;

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    ); // Show loading spinner
  }

  if (error) {
    return <div className="error">{error}</div>; // Show error message if fetch fails
  }

  // Check if no record exists for this zone/project
  if (!loading && !jobDetails.ID) {
    return (
      <NoRecordFoundAlert 
        sectionName={sectionName} 
        zone={zone || activetab} 
      />
    );
  }

  return (
    <div>
      {error ? (
        <p style={{ color: "red" }}>Error: {error}</p>
      ) : !formData ? (
        <p>Loading...</p>
      ) : (
        <div>
          <div>
            {showNotification && (
              <div
                className={`save-notification ${
                  saveStatus.includes("Failed") ? "error" : ""
                }`}
              >
                {saveStatus}
              </div>
            )}
          </div>
          <Form
            onSubmit={handleSubmit}
            className="general-mobile-single-column general-specs-form mt-4"
          >
            <Row className="mb-3 row-cols-1 row-cols-sm-2">
              <Col>
                <Form.Group as={Row} controlId="stationType">
                  <Form.Label column md={3}>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("StationType")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Station Type</span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Col md={9}>
                    <Form.Select
                      name="stationType"
                      value={formData.stationType}
                      onFocus={handleFocus} // Capture the current value on focus
                      onBlur={(e) =>
                        handleBlur(
                          e,
                          "Station Type",
                          stationTypeOptions, // You can pass any options here if needed
                          {
                            ID: formData.ID,
                            url: apiUrl,
                            field: "StationType",
                            tableKey,
                          }
                        )
                      }
                      disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                      className={fieldClasses.stationType} // Updated to use fieldClasses
                      onChange={handleChange}
                    >
                      <option value="">Select Station Type</option>
                      {stationTypeOptions.map((option) => (
                        <option key={option.ID} value={option.DisplayText}>
                          {option.DisplayText}
                        </option>
                      ))}
                      {!isValueInDropdown(
                        formData.stationType,
                        stationTypeOptions
                      ) &&
                        formData.stationType && (
                          <option
                            value={formData.stationType}
                            style={{ color: "red" }}
                          >
                            *{formData.stationType}
                          </option>
                        )}
                    </Form.Select>
                  </Col>
                </Form.Group>
              </Col>
              <Col>
                <Form.Group as={Row} controlId="siteVoltage">
                  <Form.Label column md={3}>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("SiteVoltage")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Site Voltage</span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Col>
                    <Form.Select
                      name="siteVoltage"
                      value={formData.siteVoltage}
                      onFocus={handleFocus} // Capture the current value on focus
                      onBlur={(e) =>
                        handleBlur(
                          e,
                          "Site Voltage",
                          siteVoltageOptions, // You can pass any options here if needed
                          {
                            ID: formData.ID,
                            url: apiUrl,
                            field: "SiteVoltage",
                            tableKey,
                          }
                        )
                      }
                      disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                      className={fieldClasses.siteVoltage} // Updated to use fieldClasses
                      onChange={handleChange}
                    >
                      <option value="">Select Site Voltage</option>
                      {siteVoltageOptions.map((option) => (
                        <option key={option.ID} value={option.DisplayText}>
                          {option.DisplayText}
                        </option>
                      ))}
                      {!isValueInDropdown(
                        formData.siteVoltage,
                        siteVoltageOptions
                      ) &&
                        formData.siteVoltage && (
                          <option
                            value={formData.siteVoltage}
                            style={{ color: "red" }}
                          >
                            *{formData.siteVoltage}
                          </option>
                        )}
                    </Form.Select>
                  </Col>
                </Form.Group>
              </Col>
            </Row>
            {formData.stationType === "Retrofit Kit" && (
              <Row className="mb-3">
                <Col>
                  <Form.Group as={Row} controlId="retrofitKitType">
                    <Form.Label column md={3}>
                      <OverlayTrigger
                        placement="top"
                        overlay={renderTooltip("RetrofitKitType")}
                        delay={{ show: 300, hide: 200 }}
                      >
                        <span>Retrofit Kit Type</span>
                      </OverlayTrigger>
                    </Form.Label>
                    <Col md={9}>
                      <Form.Select
                        name="retrofitKitType"
                        value={formData.retrofitKitType}
                        onFocus={handleFocus}
                        onBlur={(e) =>
                          handleBlur(
                            e,
                            "Retrofit Kit Type",
                            retrofitKitTypeOptions,
                            {
                              ID: formData.ID,
                              url: apiUrl,
                              field: "RetrofitKitType",
                              tableKey,
                            }
                          )
                        }
                        disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                        className={fieldClasses.retrofitKitType} // Updated to use fieldClasses
                        onChange={handleChange}
                      >
                        <option value="">Select Retrofit Kit Type</option>
                        {retrofitKitTypeOptions.map((option) => (
                          <option key={option.ID} value={option.DisplayText}>
                            {option.DisplayText}
                          </option>
                        ))}
                        {!isValueInDropdown(
                          formData.retrofitKitType,
                          retrofitKitTypeOptions
                        ) &&
                          formData.retrofitKitType && (
                            <option
                              value={formData.retrofitKitType}
                              style={{ color: "red" }}
                            >
                              *{formData.retrofitKitType}
                            </option>
                          )}
                      </Form.Select>
                    </Col>
                  </Form.Group>
                </Col>
                <Col />
              </Row>
            )}

            {shouldShowStationConfiguration && (
              <Row className="mb-3">
                <Col>
                  <Form.Group as={Row} controlId="stationConfiguration">
                    <Form.Label column md={3}>
                      <OverlayTrigger
                        placement="top"
                        overlay={renderTooltip("StationConfiguration")}
                        delay={{ show: 300, hide: 200 }}
                      >
                        <span>Station Configuration</span>
                      </OverlayTrigger>
                    </Form.Label>
                    <Col>
                      <Form.Select
                        name="stationConfiguration"
                        value={formData.stationConfiguration}
                        onFocus={handleFocus} // Capture the current value on focus
                        onBlur={(e) =>
                          handleBlur(
                            e,
                            "Station Configuration",
                            stationConfigurationOptions, // You can pass any options here if needed
                            {
                              ID: formData.ID,
                              url: apiUrl,
                              field: "StationConfiguration",
                              tableKey,
                            }
                          )
                        }
                        disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                        className={fieldClasses.stationConfiguration} // Updated to use fieldClasses
                        onChange={handleChange}
                      >
                        <option value="">Select Station Configuration</option>
                        {stationConfigurationOptions.map((option) => (
                          <option key={option.ID} value={option.DisplayText}>
                            {option.DisplayText}
                          </option>
                        ))}
                        {!isValueInDropdown(
                          formData.stationConfiguration,
                          stationConfigurationOptions
                        ) &&
                          formData.stationConfiguration && (
                            <option
                              value={formData.stationConfiguration}
                              style={{ color: "red" }}
                            >
                              *{formData.stationConfiguration}
                            </option>
                          )}
                      </Form.Select>
                    </Col>
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group as={Row} controlId="powerConfiguration">
                    <Form.Label column md={3}>
                      <OverlayTrigger
                        placement="top"
                        overlay={renderTooltip("PowerConfiguration")}
                        delay={{ show: 300, hide: 200 }}
                      >
                        <span>Power Configuration</span>
                      </OverlayTrigger>
                    </Form.Label>
                    <Col>
                      <Form.Select
                        name="powerConfiguration"
                        value={formData.powerConfiguration}
                        onFocus={handleFocus}
                        onBlur={(e) =>
                          handleBlur(
                            e,
                            "Power Configuration",
                            powerConfigurationOptions,
                            {
                              ID: formData.ID,
                              url: apiUrl,
                              field: "PowerConfiguration",
                              tableKey,
                            }
                          )
                        }
                        disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                        className={fieldClasses.powerConfiguration} // Updated to use fieldClasses
                        onChange={handleChange}
                      >
                        <option value="">Select Power Configuration</option>
                        {powerConfigurationOptions.map((option) => (
                          <option key={option.ID} value={option.DisplayText}>
                            {option.DisplayText}
                          </option>
                        ))}
                        {!isValueInDropdown(
                          formData.powerConfiguration,
                          powerConfigurationOptions
                        ) &&
                          formData.powerConfiguration && (
                            <option
                              value={formData.powerConfiguration}
                              style={{ color: "red" }}
                            >
                              *{formData.powerConfiguration}
                            </option>
                          )}
                      </Form.Select>
                    </Col>
                  </Form.Group>
                </Col>
              </Row>
            )}
            <Row className="mb-3">
              {/* Only show Prime System if station configuration is Suction Lift or we're not showing station configuration, and not a Vertical Turbine type */}
              {(showFeetOfLift || !shouldShowStationConfiguration) && !isVerticalTurbineType && (
                <Col>
                  <Form.Group as={Row} controlId="primeSystem">
                    <Form.Label column md={3}>
                      <OverlayTrigger
                        placement="top"
                        overlay={renderTooltip("PrimeSystem")}
                        delay={{ show: 300, hide: 200 }}
                      >
                        <span>Prime System</span>
                      </OverlayTrigger>
                    </Form.Label>
                    <Col>
                      <Form.Select
                        name="primeSystem"
                        value={formData.primeSystem}
                        onFocus={handleFocus} // Capture the current value on focus
                        onBlur={(e) =>
                          handleBlur(
                            e,
                            "Prime System",
                            primeSystemOptions, // You can pass any options here if needed
                            {
                              ID: formData.ID,
                              url: apiUrl,
                              field: "PrimeSystem",
                              tableKey,
                            }
                          )
                        }
                        disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                        className={fieldClasses.primeSystem} // Updated to use fieldClasses
                        onChange={handleChange}
                      >
                        <option value="">Select Prime System</option>
                        {primeSystemOptions.map((option) => (
                          <option key={option.ID} value={option.DisplayText}>
                            {option.DisplayText}
                          </option>
                        ))}
                        {!isValueInDropdown(
                          formData.primeSystem,
                          primeSystemOptions
                        ) &&
                          formData.primeSystem && (
                            <option
                              value={formData.primeSystem}
                              style={{ color: "red" }}
                            >
                              *{formData.primeSystem}
                            </option>
                          )}
                      </Form.Select>
                    </Col>
                  </Form.Group>
                </Col>
              )}
              {/* Add empty Col when Prime System is hidden */}
              {!((showFeetOfLift || !shouldShowStationConfiguration) && !isVerticalTurbineType) && <Col />}
              <Col>
                <Form.Group as={Row} controlId="sccrRating">
                  <Form.Label column md={3}>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("SCCRRating")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>SCCR Rating</span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Col>
                    <Form.Select
                      name="sccrRating"
                      value={formData.sccrRating}
                      onFocus={handleFocus}
                      onBlur={(e) =>
                        handleBlur(e, "SCCR Rating", sccrRatingOptions, {
                          ID: formData.ID,
                          url: apiUrl,
                          field: "SCCRRating",
                          tableKey,
                        })
                      }
                      disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                      className={fieldClasses.sccrRating} // Updated to use fieldClasses
                      onChange={handleChange}
                    >
                      <option value="">Select SCCR Rating</option>
                      {sccrRatingOptions.map((option) => (
                        <option key={option.ID} value={option.DisplayText}>
                          {option.DisplayText}
                        </option>
                      ))}
                      {!isValueInDropdown(
                        formData.sccrRating,
                        sccrRatingOptions
                      ) &&
                        formData.sccrRating && (
                          <option
                            value={formData.sccrRating}
                            style={{ color: "red" }}
                          >
                            *{formData.sccrRating}
                          </option>
                        )}
                    </Form.Select>
                  </Col>
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-3">
              {/* Only show Dynamic Inlet PSI if station configuration is Booster or Flooded Suction */}
              {(showDynamicInletPSI || !shouldShowStationConfiguration) && !showFeetOfLift && (
                <Col>
                  <Form.Group as={Row} controlId="dynamicInletPSI">
                    <Form.Label column md={3}>
                      <OverlayTrigger
                        placement="top"
                        overlay={renderTooltip("DynamicInletPSI")}
                        delay={{ show: 300, hide: 200 }}
                      >
                        <span>Dynamic Inlet PSI</span>
                      </OverlayTrigger>
                    </Form.Label>
                    <Col md={9}>
                      <Form.Control
                        type="text"
                        name="dynamicInletPSI"
                        value={formData.dynamicInletPSI}
                        onFocus={handleFocus}
                        onBlur={(e) =>
                          handleBlur(e, "Dynamic Inlet PSI", [], {
                            ID: formData.ID,
                            url: apiUrl,
                            field: "DynamicInletPSI",
                            tableKey,
                          })
                        }
                        disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                        className={fieldClasses.dynamicInletPSI} // Updated to use fieldClasses
                        onChange={handleChange}
                        placeholder="Enter Dynamic Inlet PSI"
                      />
                    </Col>
                  </Form.Group>
                </Col>
              )}
              {/* Add empty Col when Dynamic Inlet PSI is hidden */}
              {!((showDynamicInletPSI || !shouldShowStationConfiguration) && !showFeetOfLift) && <Col />}
              <Col>
                <Form.Group as={Row} controlId="fccType">
                  <Form.Label column md={3}>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("FCCType")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>FCC Type</span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Col md={9}>
                    <Form.Select
                      name="fccType"
                      value={formData.fccType}
                      onFocus={handleFocus}
                      onBlur={(e) =>
                        handleBlur(e, "FCC Type", fccTypeOptions, {
                          ID: formData.ID,
                          url: apiUrl,
                          field: "FCCType",
                          tableKey,
                        })
                      }
                      disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                      className={fieldClasses.fccType} // Updated to use fieldClasses
                      onChange={handleChange}
                    >
                      <option value="">Select FCC Type</option>
                      {fccTypeOptions.map((option) => (
                        <option key={option.ID} value={option.DisplayText}>
                          {option.DisplayText}
                        </option>
                      ))}
                      {!isValueInDropdown(formData.fccType, fccTypeOptions) &&
                        formData.fccType && (
                          <option
                            value={formData.fccType}
                            style={{ color: "red" }}
                          >
                            *{formData.fccType}
                          </option>
                        )}
                    </Form.Select>
                  </Col>
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col>
                <Form.Group as={Row} controlId="regulatoryCompliance">
                  <Form.Label column md={3}>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("RegulatoryCompliance")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Regulatory Compliance</span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Col>
                    <Form.Select
                      name="regulatoryCompliance"
                      value={formData.regulatoryCompliance}
                      onFocus={handleFocus}
                      onBlur={(e) =>
                        handleBlur(
                          e,
                          "Regulatory Compliance",
                          regulatoryComplianceOptions,
                          {
                            ID: formData.ID,
                            url: apiUrl,
                            field: "RegulatoryCompliance",
                            tableKey,
                          }
                        )
                      }
                      disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                      className={fieldClasses.regulatoryCompliance} // Updated to use fieldClasses
                      onChange={handleChange}
                    >
                      <option value="">Select Regulatory Compliance</option>
                      {regulatoryComplianceOptions.map((option) => (
                        <option key={option.ID} value={option.DisplayText}>
                          {option.DisplayText}
                        </option>
                      ))}
                      {!isValueInDropdown(
                        formData.regulatoryCompliance,
                        regulatoryComplianceOptions
                      ) &&
                        formData.regulatoryCompliance && (
                          <option
                            value={formData.regulatoryCompliance}
                            style={{ color: "red" }}
                          >
                            *{formData.regulatoryCompliance}
                          </option>
                        )}
                    </Form.Select>
                  </Col>
                </Form.Group>
              </Col>
              {/* Show Feet of Lift if station configuration is Suction Lift */}
              {(showFeetOfLift || (!shouldShowStationConfiguration && !showDynamicInletPSI)) && (
                <Col>
                  <Form.Group as={Row} controlId="feetOfLift">
                    <Form.Label column md={3}>
                      <OverlayTrigger
                        placement="top"
                        overlay={renderTooltip("FeetOfLift")}
                        delay={{ show: 300, hide: 200 }}
                      >
                        <span>Feet Of Lift</span>
                      </OverlayTrigger>
                    </Form.Label>
                    <Col md={9}>
                      <Form.Control
                        type="text"
                        name="feetOfLift"
                        value={formData.feetOfLift}
                        onFocus={handleFocus}
                        onBlur={(e) =>
                          handleBlur(e, "Feet Of Lift", [], {
                            ID: formData.ID,
                            url: apiUrl,
                            field: "FeetOfLift",
                            tableKey,
                          })
                        }
                        disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                        className={fieldClasses.feetOfLift} // Updated to use fieldClasses
                        onChange={handleChange}
                        placeholder="Enter Feet Of Lift"
                      />
                    </Col>
                  </Form.Group>
                </Col>
              )}
              {/* Add empty Col when Feet of Lift is hidden */}
              {!(showFeetOfLift || (!shouldShowStationConfiguration && !showDynamicInletPSI)) && <Col />}
            </Row>
            <Row className="mb-3">
              <Col>
                <Form.Group as={Row} controlId="stationLocation">
                  <Form.Label column md={3}>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("StationLocation")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Station Location</span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Col md={9}>
                    <Form.Select
                      name="stationLocation"
                      value={formData.stationLocation}
                      onFocus={handleFocus}
                      onBlur={(e) =>
                        handleBlur(
                          e,
                          "Station Location",
                          stationLocationOptions,
                          {
                            ID: formData.ID,
                            url: apiUrl,
                            field: "StationLocation",
                            tableKey,
                          }
                        )
                      }
                      disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                      className={fieldClasses.stationLocation} // Updated to use fieldClasses
                      onChange={handleChange}
                    >
                      <option value="">Select Station Location</option>
                      {stationLocationOptions.map((option) => (
                        <option key={option.ID} value={option.DisplayText}>
                          {option.DisplayText}
                        </option>
                      ))}
                      {!isValueInDropdown(
                        formData.stationLocation,
                        stationLocationOptions
                      ) &&
                        formData.stationLocation && (
                          <option
                            value={formData.stationLocation}
                            style={{ color: "red" }}
                          >
                            *{formData.stationLocation}
                          </option>
                        )}
                    </Form.Select>
                  </Col>
                </Form.Group>
              </Col>
              <Col>
                <Form.Group as={Row} controlId="siteElevation">
                  <Form.Label column md={3}>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("SiteElevation")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Site Elevation</span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Col md={9}>
                    <Form.Control
                      type="text"
                      name="siteElevation"
                      value={formData.siteElevation}
                      onFocus={handleFocus}
                      onBlur={(e) =>
                        handleBlur(e, "Site Elevation", [], {
                          ID: formData.ID,
                          url: apiUrl,
                          field: "SiteElevation",
                          tableKey,
                        })
                      }
                      disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                      className={fieldClasses.siteElevation} // Updated to use fieldClasses
                      onChange={handleChange}
                      placeholder="Enter Site Elevation"
                    />
                  </Col>
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col>
                <Form.Group as={Row} controlId="color">
                  <Form.Label column md={3}>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("Color")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Color</span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Col md={9}>
                    <Form.Select
                      name="color"
                      value={formData.color}
                      onFocus={handleFocus}
                      onBlur={(e) =>
                        handleBlur(e, "Color", colorOptions, {
                          ID: formData.ID,
                          url: apiUrl,
                          field: "Color",
                          tableKey,
                        })
                      }
                      disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                      className={fieldClasses.color} // Updated to use fieldClasses
                      onChange={handleChange}
                    >
                      <option value="">Select Color</option>
                      {colorOptions.map((option) => (
                        <option key={option.ID} value={option.DisplayText}>
                          {option.DisplayText}
                        </option>
                      ))}
                      {!isValueInDropdown(formData.color, colorOptions) &&
                        formData.color && (
                          <option
                            value={formData.color}
                            style={{ color: "red" }}
                          >
                            *{formData.color}
                          </option>
                        )}
                    </Form.Select>
                  </Col>
                </Form.Group>
              </Col>
              <Col>
                <Form.Group as={Row} controlId="flowMeterLocation">
                  <Form.Label column md={3}>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("FlowMeterLocation")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Flow Meter Location</span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Col>
                    <Form.Select
                      name="flowMeterLocation"
                      value={formData.flowMeterLocation}
                      onFocus={handleFocus}
                      onBlur={(e) =>
                        handleBlur(
                          e,
                          "Flow Meter Location",
                          flowMeterLocationOptions,
                          {
                            ID: formData.ID,
                            url: apiUrl,
                            field: "FlowMeterLocation",
                            tableKey,
                          }
                        )
                      }
                      disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                      className={fieldClasses.flowMeterLocation} // Updated to use fieldClasses
                      onChange={handleChange}
                    >
                      <option value="">Select Flow Meter Location</option>
                      {flowMeterLocationOptions.map((option) => (
                        <option key={option.ID} value={option.DisplayText}>
                          {option.DisplayText}
                        </option>
                      ))}
                      {!isValueInDropdown(
                        formData.flowMeterLocation,
                        flowMeterLocationOptions
                      ) &&
                        formData.flowMeterLocation && (
                          <option
                            value={formData.flowMeterLocation}
                            style={{ color: "red" }}
                          >
                            *{formData.flowMeterLocation}
                          </option>
                        )}
                    </Form.Select>
                  </Col>
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col>
                <Form.Group as={Row} controlId="trackerType">
                  <Form.Label column md={3}>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("TrackerType")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Tracker Type</span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Col md={9}>
                    <Form.Select
                      name="trackerType"
                      value={formData.trackerType}
                      onFocus={handleFocus}
                      onBlur={(e) =>
                        handleBlur(e, "Tracker Type", trackerTypeOptions, {
                          ID: formData.ID,
                          url: apiUrl,
                          field: "TrackerType",
                          tableKey,
                        })
                      }
                      disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                      className={fieldClasses.trackerType} // Updated to use fieldClasses
                      onChange={handleChange}
                    >
                      <option value="">Select Tracker Type</option>
                      {trackerTypeOptions.map((option) => (
                        <option key={option.ID} value={option.DisplayText}>
                          {option.DisplayText}
                        </option>
                      ))}
                      {!isValueInDropdown(
                        formData.trackerType,
                        trackerTypeOptions
                      ) &&
                        formData.trackerType && (
                          <option
                            value={formData.trackerType}
                            style={{ color: "red" }}
                          >
                            *{formData.trackerType}
                          </option>
                        )}
                    </Form.Select>
                  </Col>
                </Form.Group>
              </Col>
              <Col>
                <Form.Group as={Row} controlId="dischargePSI">
                  <Form.Label column md={3}>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("DischargePSI")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Discharge PSI</span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Col>
                    <Form.Control
                      type="text"
                      name="dischargePSI"
                      value={formData.dischargePSI}
                      onFocus={handleFocus}
                      onBlur={(e) =>
                        handleBlur(e, "Discharge PSI", [], {
                          ID: formData.ID,
                          url: apiUrl,
                          field: "DischargePSI",
                          tableKey,
                        })
                      }
                      disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                      className={fieldClasses.dischargePSI} // Updated to use fieldClasses
                      onChange={handleChange}
                      placeholder="Enter Discharge PSI"
                    />
                  </Col>
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col>
                <Form.Group as={Row} controlId="totalGPM">
                  <Form.Label column md={3}>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("TotalGPM")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Total GPM</span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Col>
                    <Form.Control
                      type="text"
                      name="totalGPM"
                      value={formData.totalGPM}
                      onFocus={handleFocus}
                      onBlur={(e) =>
                        handleBlur(e, "Total GPM", [], {
                          ID: formData.ID,
                          url: apiUrl,
                          field: "TotalGPM",
                          tableKey,
                        })
                      }
                      disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                      className={fieldClasses.totalGPM} // Updated to use fieldClasses
                      onChange={handleChange}
                      placeholder="Enter Total GPM"
                    />
                  </Col>
                </Form.Group>
              </Col>
              {/* Only show Wet Well Depth if we're not showing Station Configuration */}
              {shouldShowWetWellDepth && (
                <Col>
                  <Form.Group as={Row} controlId="wetWellDepth">
                    <Form.Label column md={3}>
                      <OverlayTrigger
                        placement="top"
                        overlay={renderTooltip("WetWellDepth")}
                        delay={{ show: 300, hide: 200 }}
                      >
                        <span>Wet Well Depth (in)</span>
                      </OverlayTrigger>
                    </Form.Label>
                    <Col>
                      <Form.Control
                        type="text"
                        inputMode="text"
                        name="wetWellDepth"
                        value={formData.wetWellDepth}
                        onFocus={handleFocus}
                        onBlur={handleWetWellDepthBlur}
                        disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                        className={fieldClasses.wetWellDepth} // Updated to use fieldClasses
                        onChange={handleChange}
                        placeholder={'Enter Wet Well Depth (e.g., 13\' 2", 158, TBD, or N/A)'}
                      />
                      {wetWellDepthDisplay && (
                        <Form.Text className="text-muted">
                          {wetWellDepthDisplay}
                        </Form.Text>
                      )}
                    </Col>
                  </Form.Group>
                </Col>
              )}
              {!shouldShowWetWellDepth && <Col />} {/* Empty Col for layout when Wet Well Depth is hidden */}
            </Row>
            <Row className="mb-3">
              <Col>
                <Form.Group as={Row} controlId="electricalEnclosureLocation">
                  <Form.Label column md={3}>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("ElectricalEnclosureLocation")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Electrical Enclosure Location</span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Col>
                    <Form.Select
                      name="electricalEnclosureLocation"
                      value={formData.electricalEnclosureLocation}
                      onFocus={handleFocus}
                      onBlur={(e) =>
                        handleBlur(
                          e,
                          "Electrical Enclosure Location",
                          electricalEnclosureLocationOptions,
                          {
                            ID: formData.ID,
                            url: apiUrl,
                            field: "ElectricalEnclosureLocation",
                            tableKey,
                          }
                        )
                      }
                      disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                      className={fieldClasses.electricalEnclosureLocation} // Updated to use fieldClasses
                      onChange={handleChange}
                    >
                      <option value="">
                        Select Electrical Enclosure Location
                      </option>
                      {electricalEnclosureLocationOptions.map((option) => (
                        <option key={option.ID} value={option.DisplayText}>
                          {option.DisplayText}
                        </option>
                      ))}
                      {!isValueInDropdown(
                        formData.electricalEnclosureLocation,
                        electricalEnclosureLocationOptions
                      ) &&
                        formData.electricalEnclosureLocation && (
                          <option
                            value={formData.electricalEnclosureLocation}
                            style={{ color: "red" }}
                          >
                            *{formData.electricalEnclosureLocation}
                          </option>
                        )}
                    </Form.Select>
                  </Col>
                </Form.Group>
              </Col>
              <Col>
                {/* This empty Col is needed to maintain layout consistency */}
              </Col>
            </Row>
            <Row className="general-form-full-width mb-3">
              <Col className="flex-grow-3">
                <Form.Group controlId="comments">
                  <Form.Label>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("Comments")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Comments</span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    name="comments"
                    value={formData.comments}
                    onFocus={handleFocus}
                    onBlur={(e) =>
                      handleBlur(e, "Comments", [], {
                        ID: formData.ID,
                        url: apiUrl,
                        field: "Comments",
                        tableKey,
                      })
                    }
                    disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                    className={fieldClasses.comments} // Updated to use fieldClasses
                    onChange={handleChange}
                    placeholder="Enter Comments"
                    rows={5} // Adjust the number of rows to make it larger
                  />
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </div>
      )}
    </div>
  );
}

export default JobGeneralSpecsRF;
